import Select, { CSSObjectWithLabel, ControlProps } from 'react-select';

import { InputState, InputWrapper, Link } from '../textInput';
import ChevronDown from '../svg/ChevronDown';
import clsx from 'clsx';
import { getOutlineColor } from '../inputField/utils';
import { colors } from '../foundation';

type SelectAttributes = React.SelectHTMLAttributes<HTMLSelectElement>;

export type Option = {
  value: string | number;
  label: string;
};

type Props = {
  id: SelectAttributes['id'];
  disabled?: SelectAttributes['disabled'];
  placeholder?: string;
  label?: string;
  subText?: string;
  helperText?: string;
  link?: Link;
  inputState?: InputState;
  options: Option[];
  selectedOption: Option | null;
  onChange: (option: Option | null) => void;
  isSearchable?: boolean;
};

const controlClasses =
  'w-full peer rounded-28 !bg-red-100 h-56 px-12 py-8 text-center text-white-100 caret-transparent cursor-pointer';
const disabledControlClasses = 'pointer-events-auto cursor-not-allowed';
const menuClasses = 'shadow-lg z-10 rounded-28 mt-8 overflow-hidden';
const menuListClasses = 'rounded-6';
const optionClasses =
  'hover:bg-monochrome-2 text-center bg-white-100 active:bg-monochrome-2 p-[12px] pb-0 [&:last-child]:pb-[12px] [&:not(:last-child)]:after:border-b-1 [&:not(:last-child)]:after:block [&:not(:last-child)]:after:mt-[12px] after:border-monochrome-3';

const Dropdown = ({
  id,
  disabled,
  placeholder = 'Veldu...',
  label,
  subText,
  helperText,
  link,
  inputState,
  options,
  selectedOption,
  isSearchable = true,
  onChange,
}: Props) => {
  return (
    <InputWrapper id={id} label={label} subText={subText} helperText={helperText} link={link} inputState={inputState}>
      <Select
        placeholder={placeholder}
        defaultValue={selectedOption}
        onChange={(option: Option | null) => onChange(option as Option | null)}
        options={options}
        unstyled
        isSearchable={isSearchable}
        isDisabled={disabled}
        styles={{
          control: (provided: CSSObjectWithLabel, state: ControlProps<Option, false>) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { outline, cursor, ...rest } = provided;
            return { ...rest, backgroundColor: state.isDisabled ? colors.monochrome[2] : colors.primary.input };
          },
        }}
        classNames={{
          control: ({ isDisabled }: { isDisabled: boolean }) =>
            clsx(controlClasses, getOutlineColor(inputState?.type), isDisabled && disabledControlClasses),
          menu: () => menuClasses,
          menuList: () => menuListClasses,
          placeholder: () => 'text-[#FEF4F5] label-lg',
          option: ({ isFocused, isSelected }: { isFocused: boolean; isSelected: boolean }) =>
            clsx(isFocused && 'bg-monochrome-2', isSelected && 'bg-monochrome-2 font-bold', optionClasses),
        }}
        components={{
          DropdownIndicator: () => (
            <div className="px-16">
              <ChevronDown stroke="#FEF4F5" />
            </div>
          ),
          IndicatorSeparator: () => null,
        }}
      />
    </InputWrapper>
  );
};

export default Dropdown;
